import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Activity,
  ArrowLeft,
  BookOpen,
  ChefHat,
  Compass,
  Pencil,
  Plus,
  Search,
  Settings2,
  SlidersHorizontal,
  Trash2,
  X,
} from "lucide-react";
import LoadingOverlay from "../../components/Loading/LoadingOverlay";
import Modal from "../../components/Dialog/Modal";
import CustomAlert from "../../components/Alerts/CustomAlert";
import { addAlert, removeAlert } from "../../Utils/helper";
import {
  deleteRecipeResource,
  getApiError,
  listRecipeExperts,
  listRecipeResource,
  listRecipes,
  saveRecipeResource,
} from "../../Utils/recipeAdminApi";
import RecipeSectionHelp from "./RecipeSectionHelp";
import RecipeResourceField from "./RecipeResourceField";
import { RESOURCE_CONFIG, RESOURCE_TABS } from "./recipeResourceConfig";
import "./RecipesAdmin.css";

const GROUP_ICONS = {
  Catalogue: BookOpen,
  "Recipe content": ChefHat,
  Discovery: Compass,
  "Home experience": SlidersHorizontal,
  Engagement: Activity,
  "Cooking & plans": ChefHat,
  Configuration: Settings2,
};

const GROUPED_TABS = RESOURCE_TABS.reduce((groups, tab) => {
  groups[tab.group] = [...(groups[tab.group] || []), tab];
  return groups;
}, {});

const initialFieldValue = (field) => {
  if (field.type === "json") {
    return field.default === undefined
      ? ""
      : JSON.stringify(field.default, null, 2);
  }
  if (field.type === "multi-resource") return field.default || [];
  if (field.type === "checkbox") return field.default ?? false;
  return field.default ?? "";
};

const blankFor = (config) =>
  Object.fromEntries(
    config.fields
      .filter((field) => field.type !== "file")
      .map((field) => [field.key, initialFieldValue(field)])
  );

const editFieldValue = (field, value) => {
  if (value === null || value === undefined) return initialFieldValue(field);
  if (field.type === "json") return JSON.stringify(value, null, 2);
  if (field.type === "datetime-local") return String(value).slice(0, 16);
  if (field.type === "multi-resource") return Array.isArray(value) ? value : [];
  return value;
};

const RecipeSetup = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const activeTab = RESOURCE_CONFIG[requestedTab] ? requestedTab : "categories";
  const config = RESOURCE_CONFIG[activeTab];
  const [items, setItems] = useState([]);
  const [references, setReferences] = useState({});
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(() => blankFor(config));
  const [file, setFile] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const notify = (title, message, type) =>
    addAlert(title, message, type, true, setAlerts);

  const loadReference = async (resource) => {
    if (resource === "recipes") {
      return (await listRecipes({ page_size: 500 })).items;
    }
    if (resource === "experts") return listRecipeExperts({ page_size: 500 });
    return (await listRecipeResource(resource, { page_size: 500 })).items;
  };

  const load = async () => {
    setIsLoading(true);
    try {
      const resources = [
        ...new Set(
          config.fields
            .map((field) => field.resource)
            .filter(Boolean)
        ),
      ];
      const [response, ...lookups] = await Promise.all([
        listRecipeResource(activeTab, { page_size: 500 }),
        ...resources.map(loadReference),
      ]);
      setItems(response.items);
      setReferences(
        Object.fromEntries(
          resources.map((resource, index) => [resource, lookups[index]])
        )
      );
    } catch (error) {
      notify("Could not load recipe administration", getApiError(error), "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    setEditing(null);
    setSearch("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) =>
      Object.values(item).some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(query)
      )
    );
  }, [items, search]);

  const openCreate = () => {
    setEditing({ id: null });
    setForm(blankFor(config));
    setFile(null);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm(
      Object.fromEntries(
        config.fields
          .filter((field) => field.type !== "file")
          .map((field) => [field.key, editFieldValue(field, item[field.key])])
      )
    );
    setFile(null);
  };

  const buildPayload = () => {
    const payload = {};
    for (const field of config.fields) {
      if (field.type === "file") continue;
      const value = form[field.key];
      if ((value === "" || value === undefined) && field.omitWhenBlank) continue;
      if (field.type === "json") {
        if (value === "" && field.omitWhenBlank) continue;
        try {
          payload[field.key] = typeof value === "string" ? JSON.parse(value) : value;
        } catch {
          throw new Error(`${field.label} must contain valid JSON.`);
        }
      } else if (field.type === "number") {
        if (value === "") {
          if (field.nullable) payload[field.key] = null;
        } else {
          payload[field.key] = Number(value);
        }
      } else if (field.type === "resource") {
        payload[field.key] = value === "" ? null : value;
      } else if (field.type === "datetime-local") {
        payload[field.key] = value ? new Date(value).toISOString() : null;
      } else if (field.type === "date") {
        payload[field.key] = value || null;
      } else {
        payload[field.key] = value;
      }
    }
    return payload;
  };

  const save = async () => {
    const missing = config.fields.find((field) => {
      if (!field.required || field.type === "file") return false;
      const value = form[field.key];
      return value === "" || value === null || value === undefined;
    });
    if (missing) {
      notify("Required field missing", `${missing.label} is required.`, "error");
      return;
    }

    try {
      const payload = buildPayload();
      await saveRecipeResource(
        activeTab,
        payload,
        editing?.id,
        file,
        config.fileField
      );
      notify(
        `${config.singular[0].toUpperCase()}${config.singular.slice(1)} saved`,
        "The recipe administration data is up to date.",
        "success"
      );
      setEditing(null);
      await load();
    } catch (error) {
      notify(
        "Could not save",
        error?.response ? getApiError(error) : error.message,
        "error"
      );
    }
  };

  const remove = async () => {
    if (!pendingDelete) return;
    try {
      await deleteRecipeResource(activeTab, pendingDelete.id);
      notify("Record deleted", `The ${config.singular} was removed.`, "success");
      setPendingDelete(null);
      await load();
    } catch (error) {
      notify("Could not delete", getApiError(error), "error");
    }
  };

  return (
    <div className="recipe-admin-page recipe-setup-page">
      {isLoading && <LoadingOverlay />}
      <header className="recipe-form-header">
        <button className="recipe-icon-back" onClick={() => navigate("/recipes")}>
          <ArrowLeft size={19} />
        </button>
        <div>
          <span className="recipe-eyebrow">
            <Settings2 size={15} /> Recipe administration
          </span>
          <h1>Complete recipe operations</h1>
          <p>
            All 20 supporting models from Django recipe admin, with recipes in
            the dedicated catalogue editor.
          </p>
        </div>
      </header>

      <nav className="recipe-setup-index" aria-label="Recipe administration sections">
        {Object.entries(GROUPED_TABS).map(([group, tabs]) => {
          const Icon = GROUP_ICONS[group] || Settings2;
          return (
            <div className="recipe-setup-group" key={group}>
              <span>
                <Icon size={14} /> {group}
              </span>
              <div>
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    className={activeTab === tab.key ? "is-active" : ""}
                    onClick={() => setSearchParams({ tab: tab.key })}
                  >
                    {tab.label.replace(/^Recipe /, "")}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </nav>

      <section className="recipe-panel recipe-resource-panel">
        <div className="recipe-resource-heading">
          <div>
            <span className="recipe-resource-group-label">{config.group}</span>
            <h2>{config.title}</h2>
            <p>{config.note}</p>
          </div>
          <button className="recipe-button recipe-button-primary" onClick={openCreate}>
            <Plus size={17} /> Add {config.singular}
          </button>
        </div>

        <RecipeSectionHelp title={`How to use ${config.title.toLowerCase()}`}>
          <p>{config.help.text}</p>
          <ul>
            {config.help.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </RecipeSectionHelp>

        <label className="recipe-search-field resource-search">
          <Search size={17} />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={`Search ${config.title.toLowerCase()}…`}
          />
        </label>

        <div className="recipe-resource-table-wrap">
          <table className="recipe-resource-table">
            <thead>
              <tr>
                {config.columns.map((column) => (
                  <th key={column.key}>{column.label}</th>
                ))}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <tr key={item.id}>
                  {config.columns.map((column) => (
                    <td key={column.key}>{renderCell(item[column.key], column)}</td>
                  ))}
                  <td>
                    <div className="recipe-table-actions">
                      <button onClick={() => openEdit(item)} aria-label="Edit">
                        <Pencil size={15} />
                      </button>
                      <button
                        className="is-danger"
                        onClick={() => setPendingDelete(item)}
                        aria-label="Delete"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredItems.length === 0 && (
                <tr>
                  <td colSpan={config.columns.length + 1}>
                    <div className="recipe-table-empty">
                      <BookOpen size={22} /> No records found
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {editing && (
        <div
          className="recipe-drawer-backdrop"
          role="presentation"
          onMouseDown={(event) =>
            event.target === event.currentTarget && setEditing(null)
          }
        >
          <section
            className="recipe-resource-drawer"
            role="dialog"
            aria-modal="true"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            <header>
              <div>
                <span>{editing.id ? "Edit" : "Add"}</span>
                <h2>{config.singular}</h2>
              </div>
              <button onClick={() => setEditing(null)} aria-label="Close">
                <X size={20} />
              </button>
            </header>
            <div className="recipe-drawer-body">
              <RecipeSectionHelp title={`About this ${config.singular}`} defaultOpen>
                <p>{config.help.text}</p>
              </RecipeSectionHelp>
              {config.fields.map((field) => (
                <RecipeResourceField
                  key={field.key}
                  field={field}
                  value={form[field.key]}
                  options={references[field.resource] || []}
                  existing={editing[field.key]}
                  onFile={setFile}
                  onChange={(value) =>
                    setForm((current) => ({ ...current, [field.key]: value }))
                  }
                />
              ))}
            </div>
            <footer>
              <button className="recipe-button recipe-button-secondary" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="recipe-button recipe-button-primary" onClick={save}>
                Save {config.singular}
              </button>
            </footer>
          </section>
        </div>
      )}

      {pendingDelete && (
        <Modal title={`Delete ${config.singular}?`} onClose={() => setPendingDelete(null)}>
          <p className="recipe-modal-copy">
            This record will be permanently removed. Relationships or database
            constraints may block unsafe deletion.
          </p>
          <div className="recipe-modal-actions">
            <button className="recipe-button recipe-button-secondary" onClick={() => setPendingDelete(null)}>
              Cancel
            </button>
            <button className="recipe-button recipe-button-danger" onClick={remove}>
              Delete
            </button>
          </div>
        </Modal>
      )}

      {alerts.map((alert) => (
        <CustomAlert
          key={alert.id}
          {...alert}
          onClose={() => removeAlert(alert.id, setAlerts)}
        />
      ))}
    </div>
  );
};

const renderCell = (value, column) => {
  if (column.type === "image") {
    return value ? (
      <img className="recipe-table-thumb" src={value} alt="" />
    ) : (
      <span className="recipe-table-placeholder">—</span>
    );
  }
  if (column.type === "boolean") {
    return (
      <span className={`recipe-boolean-pill ${value ? "is-true" : "is-false"}`}>
        {value ? "Yes" : "No"}
      </span>
    );
  }
  if (column.type === "dateTime") {
    return value ? new Date(value).toLocaleString() : "—";
  }
  if (column.type === "list") {
    return Array.isArray(value) && value.length ? value.join(", ") : "—";
  }
  if (typeof value === "object" && value !== null) {
    const serialized = JSON.stringify(value);
    return serialized.length > 80 ? `${serialized.slice(0, 77)}…` : serialized;
  }
  return value === null || value === undefined || value === "" ? "—" : String(value);
};

export default RecipeSetup;
